import { type Resident } from "@packages/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import React, { type ReactNode, useMemo, useRef } from "react";
import { LuClipboardCopy } from "react-icons/lu";
import { type Merge } from "type-fest";
import { useTranslate } from "../../services";
import { css, cx } from "../../styled-system/css";
import { TableStyle, Tooltip, TypographyStyle, skeletonStyle } from "../common/index";
import { copyToClipboard } from "../utilities";

const classes = TableStyle.raw({ foot: false });

export type ResidentTableProperties = Merge<
  React.ComponentPropsWithoutRef<"div">,
  {
    action?: { (resident: Resident): ReactNode };
    columns?: Readonly<Partial<Record<"action" | "agreed" | "name" | "sex" | "userKey", boolean>>>;
    loading?: boolean;
    rows?: ReadonlyArray<Resident>;
  }
>;

export function ResidentTable({
  action,
  className,
  columns,
  loading,
  rows = [],
  ...properties
}: ResidentTableProperties) {
  const translate = useTranslate();

  const parentRef = useRef<HTMLDivElement>(null);

  const gridTemplateColumns = useMemo(
    function () {
      return [
        columns?.userKey !== false ? "104px" : "",
        columns?.name !== false ? "minmax(120px, 1fr)" : "",
        columns?.sex !== false ? "40px" : "",
        columns?.agreed !== false ? "120px" : "",
        columns?.action !== false ? "64px" : "",
      ]
        .filter(function (column) {
          return column !== "";
        })
        .join(" ");
    },
    [columns],
  );

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize() {
      return 64;
    },
    getScrollElement() {
      return parentRef.current;
    },
    overscan: 20,
  });

  const headCellClass = TypographyStyle({ color: "primary/60", ellipsis: true, fontWeight: 500 });

  return (
    <div
      {...properties}
      className={cx(
        css(classes.root, TypographyStyle.raw({ fontSize: "14px", lineHeight: 1 })),
        className,
      )}
    >
      <div className={css(classes.head)}>
        <div className={css(classes.headRow)} style={{ gridTemplateColumns }}>
          {columns?.userKey !== false && (
            <div className={headCellClass}>{translate("%UserKey.0%")}</div>
          )}
          {columns?.name !== false && (
            <div className={headCellClass}>{translate("%ResidentName.0%")}</div>
          )}
          {columns?.sex !== false && <div className={headCellClass}>{translate("%Sex.0%")}</div>}
          {columns?.action !== false && (
            <div className={headCellClass}>{translate("%PrivacyPolicy.2%")}</div>
          )}
        </div>
      </div>

      <div className={css(classes.virtualOuterBody)} ref={parentRef}>
        <div
          className={css(classes.virtualInnerBody)}
          style={{ height: virtualizer.getTotalSize() + "px" }}
        >
          {virtualizer.getVirtualItems().map(function (virtualItem) {
            const resident = rows[virtualItem.index];
            return (
              <div
                className={css(classes.virtualBodyRow)}
                data-index={virtualItem.index}
                key={virtualItem.key}
                ref={virtualizer.measureElement}
                style={{
                  gridTemplateColumns,
                  height: virtualItem.size + "px",
                  transform: "translateY(" + virtualItem.start + "px)",
                }}
              >
                <div>
                  <Tooltip
                    content={
                      <div className={css({ display: "flex", alignItems: "center", gap: "8px" })}>
                        <LuClipboardCopy className={css({ height: "14px", width: "auto" })} />
                        <span
                          className={TypographyStyle({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                            monospace: true,
                          })}
                        >
                          {resident.userKey}
                        </span>
                      </div>
                    }
                    trigger={
                      <span
                        className={css(
                          TypographyStyle.raw({
                            ellipsis: true,
                            fontSize: "14px",
                            lineHeight: 1,
                            monospace: true,
                          }),
                          {
                            cursor: "pointer",
                            _hover: { fontWeight: 500, textDecoration: "underline" },
                            _active: { fontWeight: 700 },
                          },
                        )}
                        onClick={function () {
                          copyToClipboard(resident.userKey);
                        }}
                      >
                        {resident.userKey}
                      </span>
                    }
                  />
                </div>
                <div
                  className={TypographyStyle({
                    ellipsis: true,
                    fontSize: "14px",
                    lineHeight: 1,
                  })}
                >
                  {resident.name}
                  {resident.pronunciationName && (
                    <span className={TypographyStyle({ color: "primary/60", fontSize: "12px" })}>
                      &nbsp;({resident.pronunciationName})
                    </span>
                  )}
                </div>
                <div>{translate("%Sex." + resident.sex + ".0%")}</div>
                <div>
                  <span
                    className={cx(
                      css({
                        borderRadius: "32767px",
                        color: "white",
                        padding: "2px 8px",
                      }),
                      resident.agreed
                        ? css({ backgroundColor: "#0D9488" })
                        : css({ backgroundColor: "#71717A" }),
                    )}
                  >
                    {translate("%PrivacyPolicy." + resident.agreed + ".0%")}
                  </span>
                </div>
                <div>{action?.(resident)}</div>
              </div>
            );
          })}
        </div>

        {rows.length === 0 &&
          (loading ? (
            Array.from({ length: 5 }).map(function (_, index) {
              return (
                <div
                  className={css(classes.bodyRow, { padding: "16px" })}
                  key={index}
                  style={{ gridTemplateColumns }}
                >
                  <div className={css(skeletonStyle, { width: "stretch" })} />
                  <div className={css(skeletonStyle, { width: "stretch" })} />
                </div>
              );
            })
          ) : (
            <div className={css(classes.bodyRow, { padding: "16px" })}>
              <p className={css({ fontWeight: 500 })}>{translate("%NoItems.0%")}</p>
            </div>
          ))}
      </div>
    </div>
  );
}
